<template lang="pug">
    .matrix
        table.table
            tr
                th.row-label
                    .label {{ matrix.labels.row }}
                th.col-label
                    .label {{ matrix.labels.col }}
            tr(v-for="(row, i) in matrix.rows")
                th.vertical-label
                    .row-label-cell
                        .colorimage-cell(
                            v-if="row.image && row.image.src"
                            :style="{ backgroundImage: 'url(' + row.image.src + ')'}"
                        )
                        span.label {{ row.label }}
                td
                    .buttons
                        button.button.is-small.is-link(
                            v-for="col in row.columns"
                            :class="{ 'is-active': col.article && col.article.id === activeArticleId, 'is-out-of-stock': manageStock && col.article && !col.article.qty_sum }"
                            :disabled="!valid || !col.article || (manageStock && col.article.qty_sum <= col.cartQty)"
                            @click="selectArticle(row, col)"
                        ) {{ col.label }}
            tr.hr(v-if="productProperties && productProperties.length")
                td
            tr(v-for="(property, i) in productProperties")
                th.vertical-label
                    span.label {{ property.label }}
                td
                    Property(
                        :key="property.id"
                        :hide-label="true"
                        :valid-articles="validArticles"
                        :property="property"
                        :active-option="selectedOptions[property.id]"
                        @select="selectProperty"
                    )
            tr.hr(v-if="infos && infos.length")
                td
            tr(v-for="(property, i) in infos")
                th.vertical-label
                    span.label {{ property.label }}
                td(v-for="option in property.options")
                    .value {{ option.value }}
</template>

<script>
import { mapGetters } from 'vuex';
import { get, trim, round, cloneDeep } from '@/helpers';
import { moneyFormat } from '@/filters';
import { sortingOrders } from '@/extensions/shop/helpers';

import Property from '@/extensions/shop/components/LazyCategoryProducts/Partials/Property.vue';

export default {
    name: 'Matrix',
    props: {
        articleProperties: {
            type: Array,
            required: true
        },
        productProperties: {
            type: Array,
            required: false
        },
        infos: {
            type: Array,
            required: false
        },
        articles: {
            type: Array,
            required: false
        },
        validArticles: {
            type: Array,
            required: false
        },
        selectedOptions: {
            type: Object,
            required: false
        },
        manageStock: {
            type: Boolean,
            default: false
        },
        valid: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        articleSum: 0
    }),
    watch: {
        matrix() {
            this.calculateRecommendations();
        },
        articleSum() {
            this.calculateRecommendations();
        }
    },
    components: {
        Property
    },
    computed: {
        baseUrl() {
            return window.location.host;
        },
        rowProp() {
            if (this.baseUrl === 'b2b-outlet.adenauer.com') {
                return this.articleProperties[1];
            } else if (this.baseUrl === 'b2b-outlet.adenauer.com.37-61-201-233.vanameland05.de') {
                return this.articleProperties[1];
            } else {
                return this.articleProperties[0];
            }
        },

        colProp() {
            if (this.baseUrl === 'b2b-outlet.adenauer.com') {
                return this.articleProperties[0];
            } else if (this.baseUrl === 'b2b-outlet.adenauer.com.37-61-201-233.vanameland05.de') {
                return this.articleProperties[0];
            } else {
                return this.articleProperties[1];
            }
        },

        recommandations() {
            return !!this.matrix.recommendations.find(el => el.percentage !== undefined);
        },

        matrix() {
            let customerGroup, code;

            let matrix = {
                labels: {
                    row: this.rowProp.label,
                    col: this.colProp.label
                },
                recommendations: [],
                rows: []
            };

            let articleMap = {};
            if (this.articles && this.articles.length) {
                this.articles.forEach(article => {
                    // get row value
                    // to use on the first level of the articleMap

                    const rowPropertyPivot = article.propertyPivots.find(el => {
                        return el.property.id === this.rowProp.id;
                    });

                    const rowValue = get(rowPropertyPivot, 'value');
                    const rowField = get(rowPropertyPivot, 'property.propType.value_field');
                    const rowAltField = get(rowPropertyPivot, 'property.propType.value_field_alt');

                    const rowKey = rowValue[rowAltField] || rowValue[rowField];

                    // get size value
                    // to use on the second level of the articleMap

                    const colPropertyPivot = article.propertyPivots.find(el => {
                        return el.property.id === this.colProp.id;
                    });

                    const colValue = get(colPropertyPivot, 'value');
                    const colField = get(colPropertyPivot, 'property.propType.value_field');
                    const colAltField = get(colPropertyPivot, 'property.propType.value_field_alt');

                    const colKey = colValue[colAltField] || colValue[colField];

                    if (!articleMap[rowKey]) articleMap[rowKey] = {};
                    articleMap[rowKey][colKey] = article;
                });
            }

            this.rowProp.options
                .map((option, i) => {
                    const sortingOrder = get(sortingOrders, this.rowProp.key) || [];
                    let position = sortingOrder.findIndex(el => el === option.value);
                    if (position >= 0) position++;
                    else position = null;
                    option.position = position || 1000 + i;
                    return option;
                })
                .sort((a, b) => get(a, 'position', 0) - get(b, 'position', 0))
                .forEach(_row => {
                    let row = {
                        label: _row.value,
                        // image: this.getColorImage(_row.image),
                        option: _row,
                        columns: [],
                        cartQty: 0,
                        cartPrice: 0
                    };

                    this.colProp.options
                        .map((option, i) => {
                            const sortingOrder = get(sortingOrders, this.colProp.key) || [];
                            let position = sortingOrder.findIndex(el => el === option.value);
                            if (position >= 0) position++;
                            else position = null;
                            option.position = position || 1000 + i;
                            return option;
                        })
                        .sort((a, b) => get(a, 'position', 0) - get(b, 'position', 0))
                        .forEach(_col => {
                            let recommendation = {
                                col: _col.value,
                                percentage: undefined,
                                value: undefined
                            };

                            if (!matrix.recommendations.find(el => el.col === recommendation.col)) {
                                matrix.recommendations.push(recommendation);
                            }

                            // const article = get(articleMap, `${_row.value}.${_col.value}`);
                            const article = articleMap?.[_row.value]?.[_col.value];

                            // let image = this.getColorImage(_col.image);

                            let cartQty = 0;
                            let cartPrice = 0;

                            if (article) {
                                if (!row.image) {
                                    const mainMedia =
                                        article.media.find(el => {
                                            return (
                                                get(el, 'settings.show.list', true) === true &&
                                                get(el, 'type') === 'image'
                                            );
                                        }) ||
                                        article.media.find(el => {
                                            return (
                                                get(el, 'settings.show.detail', true) === true &&
                                                get(el, 'type') === 'image'
                                            );
                                        });

                                    if (mainMedia && mainMedia.image) {
                                        row.image = this.getArticleImage(mainMedia.image);
                                    }
                                }

                                this.cart.articlePivots.forEach(articlePivot => {
                                    if (articlePivot.article.id === article.id) {
                                        const qty = articlePivot.qty;
                                        cartQty += qty;

                                        let price = 0;
                                        if (this.customer && get(this.customer, 'show_net_prices')) {
                                            if (article.salePrice) {
                                                price = article.salePrice.net_value;

                                                customerGroup =
                                                    article.salePrice.customerGroup ||
                                                    get(this.cms, 'shop.defaultCustomerGroup');
                                                code = get(customerGroup, 'currency.iso4217') || 'EUR';
                                            } else if (article.price) {
                                                price = article.price?.net_value;
                                            }
                                        } else {
                                            if (article.salePrice) {
                                                price = article.salePrice.value;
                                            } else if (article.price) {
                                                price = article.price?.value;
                                            }
                                        }
                                        cartPrice += price;
                                    }
                                });

                                if (article.percentage !== null) {
                                    recommendation.percentage = article.percentage;
                                }
                            }

                            cartPrice *= cartQty;

                            row.cartQty += cartQty;
                            row.cartPrice += cartPrice;

                            cartPrice = moneyFormat(cartPrice, code);
                            let column = {
                                label: _col.value,
                                // image,
                                option: _col,
                                article,
                                cartQty,
                                cartPrice
                            };

                            row.columns.push(column);
                            // this.$set(row.columns, row.columns.length, column);
                        });

                    row.cartPrice = moneyFormat(row.cartPrice, code);
                    matrix.rows.push(row);
                    // this.$set(matrix.rows, matrix.rows.length, row);
                });

            return matrix;
        },
        activeArticleId() {
            let id;

            let allArticlePropertiesSelected = true;
            this.articleProperties.forEach(el => {
                if (!this.selectedOptions[el.id]) allArticlePropertiesSelected = false;
            });

            if (allArticlePropertiesSelected && this.validArticles && this.validArticles.length === 1) {
                id = this.validArticles[0].id;
            }
            return id;
        },
        ...mapGetters({
            cms: 'CMS',
            cart: 'shop/cart',
            customer: 'shop/customer'
        })
    },
    methods: {
        calculateRecommendations() {
            const number = parseFloat(this.articleSum);
            if (this.recommandations) {
                let sum = 0;
                this.matrix.recommendations.forEach(el => {
                    el.value = round((number / 100) * el.percentage);
                    el.dif = (number / 100) * el.percentage - el.value;
                    sum += el.value;
                });

                let difference = number - sum;
                while (difference > 0) {
                    // add
                    const obj = cloneDeep(this.matrix.recommendations).sort((a, b) => b.dif - a.dif)[0];
                    let recommendation = this.matrix.recommendations.find(el => el.col === obj.col);
                    recommendation.value++;
                    recommendation.dif = (number / 100) * recommendation.percentage - recommendation.value;
                    difference--;
                }
                while (difference < 0) {
                    // sub
                    const obj = cloneDeep(this.matrix.recommendations).sort((a, b) => b.dif - a.dif)[
                        this.matrix.recommendations.length - 1
                    ];
                    let recommendation = this.matrix.recommendations.find(el => el.col === obj.col);
                    recommendation.value--;
                    recommendation.dif = (number / 100) * recommendation.percentage - recommendation.value;
                    difference++;
                }
            }
        },
        activateArticle(row) {
            if (row.image && row.columns && row.columns.length) {
                const article = row.columns[0].article;
                this.$emit('toggle-active-article', article);
            }
        },
        getArticleImage(image) {
            if (!image || !image.source) return undefined;
            let width = image.width;
            let height = image.height;

            const aspectRatio = width / height;
            const maxHeight = 30;

            if (height > maxHeight) {
                height = maxHeight;
                width = height * aspectRatio;
            }

            const fit = `fit&h=${height}`;
            const storageRoute = trim(
                `${process.env.VUE_APP_API_HTTP}${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`,
                '/'
            );
            return {
                src: `${storageRoute}/images/${image.id}/${image.source.name}?${fit}`,
                error: `${storageRoute}/images/${image.id}/${image.source.name}?${fit}&preview=greyscale`,
                loading: `${storageRoute}/images/${image.id}/${image.source.name}?${fit}&preview=blur`,
                width,
                height,
                aspectRatio
            };
        },
        getColorImage(image) {
            if (!image || !image.source) return undefined;
            let width = image.width;
            let height = image.height;

            const aspectRatio = width / height;
            const maxHeight = 30;

            if (height > maxHeight) {
                height = maxHeight;
                width = height * aspectRatio;
            }

            const fit = `fit&h=${height}`;
            const storageRoute = trim(
                `${process.env.VUE_APP_API_HTTP}${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`,
                '/'
            );
            return {
                src: `${storageRoute}/images/${image.id}/${image.source.name}?${fit}`,
                error: `${storageRoute}/images/${image.id}/${image.source.name}?${fit}&preview=greyscale`,
                loading: `${storageRoute}/images/${image.id}/${image.source.name}?${fit}&preview=blur`,
                width,
                height,
                aspectRatio
            };
        },
        selectArticle(row, col) {
            // if only one of both has changed
            // don't trigger both
            if (
                !this.activeArticleId ||
                (this.selectedOptions[this.rowProp.id].id === row.option.id &&
                    this.selectedOptions[this.colProp.id].id === col.option.id) ||
                (this.selectedOptions[this.rowProp.id].id !== row.option.id &&
                    this.selectedOptions[this.colProp.id].id !== col.option.id)
            ) {
                this.selectProperty({ property: this.rowProp, option: row.option });
                this.selectProperty({ property: this.colProp, option: col.option });
            } else if (this.selectedOptions[this.rowProp.id].id !== row.option.id) {
                this.selectProperty({ property: this.rowProp, option: row.option });
            } else {
                this.selectProperty({ property: this.colProp, option: col.option });
            }
        },
        selectProperty(payload) {
            this.$emit('select-property', payload);
        }
    },
    mounted() {
        if (this.articleProperties.length !== 2) throw new Error('The number of article properties must be exactly 2.');
    }
};
</script>

<style lang="sass" scoped>
.matrix
  margin: -2px
table.table
  border-spacing: 2px
  border-collapse: separate
  position: relative
  z-index: 1
  .row-label-cell
    display: flex
    align-items: center
    .label
      line-height: 1

    .colorimage-cell
      margin-right: 4px
      padding: 0 !important
      width: 30px
      height: 30px
      background-position: center
      background-size: cover
      background-repeat: no-repeat

  tr.hr td
    padding-top: 0.5rem

  td.has-button
    width: 1px

  .label
    @apply font-medium

  .buttons
    margin: 0 -2px
    .button
      margin: 0 2px

  .vertical-label
    padding-right: calc(1rem - 2px)
</style>
